import { ExportIcon, DocumentIcon } from "components/atoms/Icon";
import { Paragraph } from "components/atoms/Typography";
import ViewTab from "components/atoms/ViewTab";
import Export from "components/molecules/Export/Export";
import Filters from "components/molecules/Filters/Filters";
import { useMemo } from "react";
import CsvDownloadButton from "react-json-to-csv";
import { useNavigate } from "react-router-dom";
import useFilters from "recoilStore/useFilters";

import AddPropertiesFundsModal from "./AddPropertiesFundsModal";
import AddEditTags from "./HeaderComponents/AddEditTags";
import ViewButton from "./HeaderComponents/ViewButton";
import DeleteBuildings from "./HeaderComponents/DeleteBuildings";
import RemodelBuildingsView from "./HeaderComponents/RemodelBuildingsView";
import { BuildingsTableData } from "./helpers";
import TabType from "./PortfolioPage.types";
import styles from "./ViewHeader.module.scss";

interface ViewHeaderProps {
    numberOfBuildings?: number;
    numberOfProperties?: number;
    selectedProperties: Property[];
    setSelectedProperties: React.Dispatch<React.SetStateAction<Property[]>>;
    numberOfFunds?: number;
    selectedTab: TabType;
    tabClickHandler: (tab: TabType) => void;
    buildingName?: string;
    numberOfSelectedBuildings: number;
    selectedBuildingRows: BuildingsTableData[];
    setSelectedBuildingRows: React.Dispatch<React.SetStateAction<BuildingsTableData[]>>;
    selectedTags: string[];
    selectedTagsAreMixed: boolean;
    portfolioTableData: BuildingsTableData[] | Property[];
}

function ViewHeader({
    numberOfBuildings,
    numberOfProperties,
    selectedProperties,
    setSelectedProperties,
    numberOfFunds,
    selectedTab,
    tabClickHandler,
    buildingName,
    numberOfSelectedBuildings,
    selectedBuildingRows,
    setSelectedBuildingRows,
    selectedTags,
    selectedTagsAreMixed,
    portfolioTableData,
}: ViewHeaderProps) {
    const { numberOfFilters } = useFilters();
    const navigate = useNavigate();

    const clearSelectedRows = () => {
        setSelectedBuildingRows([]);
    };

    const selectedBuildingModelUids = useMemo(() => {
        if (selectedBuildingRows.length < 1) return [];

        if ((selectedBuildingRows[0] as BuildingsTableData).buildingModelUid) {
            return (selectedBuildingRows as BuildingsTableData[]).map(({ buildingModelUid }) => buildingModelUid);
        }
        return selectedProperties.flatMap((row) => row.buildingModels.map(({ buildingModelUid }) => buildingModelUid));
    }, [selectedBuildingRows]);

    const csvExportData =
        selectedTab !== "buildings"
            ? []
            : (portfolioTableData as BuildingsTableData[]).map((row) => {
                  const { tags, dataCoverage, ...flatFields } = row;

                  return {
                      ...flatFields,
                      tags: Array.isArray(tags) ? tags.join(", ") : (tags ?? ""),
                      // Flatten dataCoverage into separate columns to avoid issue in csv export
                      dataCoverage: dataCoverage?.dataCoverage ?? "",
                      energyType: dataCoverage?.energyType ?? "",
                      energyTypeFromEquipmentData: dataCoverage?.energyTypeFromEquipmentData ?? "",
                      energyTypeFromUtilityData: dataCoverage?.energyTypeFromUtilityData ?? "",
                      equipmentDataHasSizeAndYear: dataCoverage?.equipmentDataHasSizeAndYear ?? "",
                      equipmentDataUploaded: dataCoverage?.equipmentDataUploaded ?? "",
                      modellingStatus: dataCoverage?.modellingStatus ?? "",
                      utilityDataUploaded: dataCoverage?.utilityDataUploaded ?? "",
                  };
              });

    return (
        <div
            className="view-header"
            style={{
                borderBottom: numberOfFilters > 0 ? "1px solid var(--audette-gray-300)" : undefined,
            }}
        >
            <div className="view-header__tabs">
                <ViewTab
                    id="portfolio-buildings-tab"
                    name={selectedTab === "buildings" ? `Buildings (${numberOfBuildings})` : "Buildings"}
                    selected={selectedTab === "buildings"}
                    onClick={() => tabClickHandler("buildings")}
                />
                <ViewTab
                    id="portfolio-properties-tab"
                    name={selectedTab === "properties" ? `Properties (${numberOfProperties})` : "Properties"}
                    selected={selectedTab === "properties"}
                    onClick={() => tabClickHandler("properties")}
                />
                <ViewTab
                    id="portfolio-funds-tab"
                    name={selectedTab === "funds" ? `Funds (${numberOfFunds})` : "Funds"}
                    selected={selectedTab === "funds"}
                    onClick={() => tabClickHandler("funds")}
                />
            </div>{" "}
            {selectedTab === "buildings" && <Filters />}
            {selectedBuildingRows.length !== 0 && selectedTab === "buildings" && (
                <AddPropertiesFundsModal
                    type="properties"
                    selectedProperties={selectedProperties}
                    setSelectedProperties={setSelectedProperties}
                    selectedBuildings={selectedBuildingRows}
                    setSelectedBuildings={setSelectedBuildingRows}
                />
            )}
            {selectedProperties.length !== 0 && selectedTab === "properties" && (
                <AddPropertiesFundsModal
                    type="funds"
                    selectedProperties={selectedProperties}
                    setSelectedProperties={setSelectedProperties}
                    selectedBuildings={selectedBuildingRows}
                    setSelectedBuildings={setSelectedBuildingRows}
                />
            )}
            {selectedProperties.length === 1 && selectedTab === "properties" && (
                <button
                    className="add-new-property__button"
                    type="button"
                    onClick={() => navigate(`/properties/${selectedProperties[0].propertyUid}/virtual-audit`)}
                >
                    <DocumentIcon />
                    <Paragraph>Property Virtual Audit</Paragraph>
                </button>
            )}
            {selectedBuildingRows.length !== 0 && selectedTab === "buildings" && (
                <AddEditTags
                    selectedTags={selectedTags}
                    selectedTagsAreMixed={selectedTagsAreMixed}
                    selectedBuildingRows={selectedBuildingRows as BuildingsTableData[]}
                    setSelectedBuildingRows={setSelectedBuildingRows}
                />
            )}
            {selectedTab !== "funds" && <ViewButton />}
            <Export />
            {selectedTab === "buildings" && (
                <CsvDownloadButton data={csvExportData} filename="buildingsTableData.csv" delimiter=",">
                    <div className={styles.exportButton}>
                        <ExportIcon />
                        <Paragraph>Export Table</Paragraph>
                    </div>
                </CsvDownloadButton>
            )}
            {numberOfSelectedBuildings > 0 && selectedTab === "buildings" && (
                <RemodelBuildingsView
                    selectedBuildingModelUids={selectedBuildingModelUids}
                    clearSelectedRows={clearSelectedRows}
                />
            )}
            {numberOfSelectedBuildings > 0 && selectedTab === "buildings" && (
                <DeleteBuildings
                    numberOfSelectedIds={numberOfSelectedBuildings}
                    buildingName={buildingName}
                    selectedBuildingModelUids={selectedBuildingModelUids}
                    clearSelectedRows={clearSelectedRows}
                />
            )}
        </div>
    );
}

export default ViewHeader;
